import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

export const eurFr = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const dLong = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");
const hm = (d) => d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }).replace(":", " h ");
const ST_CLS = { a_regler: "bg-amber-50 text-amber-800", partielle: "bg-sky-50 text-sky-800", reglee: "bg-emerald-50 text-emerald-700" };

export function usePoll(fetcher, deps, ms = 30000) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [at, setAt] = useState(null);
  const load = useCallback(async () => {
    try { const d = await fetcher(); setData(d); setError(false); setAt(new Date()); }
    catch { setError(true); }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, ms);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [load, ms]);
  return { data, error, at, reload: load };
}

export function Freshness({ at, error, onRetry }) {
  if (error) return <span className="flex items-center gap-1 text-xs text-amber-700" data-testid="freshness-error"><Icons.WifiOff className="h-3.5 w-3.5" />Connexion interrompue — informations peut-être pas à jour{onRetry && <button onClick={onRetry} className="ml-1 underline">Réessayer</button>}</span>;
  return at ? <span className="text-xs text-slate-400" data-testid="freshness-at">Mis à jour à {hm(at)}</span> : null;
}

async function openPdf(url, token, download, name) {
  const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
  const href = URL.createObjectURL(r.data);
  if (download) { const a = document.createElement("a"); a.href = href; a.download = name; a.click(); } else window.open(href, "_blank");
}

function ProofButton({ inv, token, onDone }) {
  const ref = useRef(null);
  const send = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await axios.post(`${API}/clientspace/invoices/${inv.id}/proof`, fd, { headers: { Authorization: `Bearer ${token}` } }); toast.success(data.message); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible"); } finally { e.target.value = ""; }
  };
  return (<>
    <input ref={ref} type="file" accept="application/pdf,image/*" hidden onChange={send} />
    <Button size="sm" variant="ghost" onClick={() => ref.current?.click()} data-testid={`cs-inv-proof-${inv.id}`}><Icons.Upload className="mr-1 h-3.5 w-3.5" />Justificatif</Button>
  </>);
}

function InvActions({ i, token, onAsk, reload }) {
  const url = `${API}/clientspace/invoices/${i.id}/pdf`;
  return (
    <div className="flex flex-wrap justify-end gap-1">
      <Button size="sm" variant="ghost" onClick={() => openPdf(url, token, false).catch(() => toast.error("Ouverture impossible"))} data-testid={`cs-inv-view-${i.id}`}><Icons.Eye className="mr-1 h-3.5 w-3.5" />Consulter</Button>
      <Button size="sm" variant="ghost" onClick={() => openPdf(url, token, true, `facture_${i.number}.pdf`).catch(() => toast.error("Téléchargement impossible"))} data-testid={`cs-inv-dl-${i.id}`}><Icons.Download className="mr-1 h-3.5 w-3.5" />Télécharger</Button>
      <Button size="sm" variant="ghost" onClick={() => onAsk(`Question concernant la facture ${i.number} (${i.title}) : `)} data-testid={`cs-inv-ask-${i.id}`}><Icons.MessageCircleQuestion className="mr-1 h-3.5 w-3.5" />Poser une question</Button>
      {i.due > 0 && <ProofButton inv={i} token={token} onDone={reload} />}
    </div>
  );
}

function InvBadges({ i }) {
  return (<>
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ST_CLS[i.status]}`} data-testid={`cs-inv-status-${i.id}`}>{i.status_label}</span>
    {i.overdue && <span className="mt-1 block w-fit rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">Échéance dépassée</span>}
    {i.proof_pending && <span className="mt-1 block w-fit rounded-full bg-violet-50 px-2 py-0.5 text-[11px] text-violet-700" data-testid={`cs-inv-proof-pending-${i.id}`}>Justificatif reçu — règlement à vérifier</span>}
  </>);
}

export function WorksiteInvoices({ wsId, token, onAsk, highlight }) {
  const { data, error, at, reload } = usePoll(() => axios.get(`${API}/clientspace/worksites/${wsId}/invoices`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data), [wsId, token]);
  if (!data && !error) return null;
  if (!data) return <Card className="p-4 text-sm text-rose-600">Factures indisponibles. <button onClick={reload} className="underline">Réessayer</button></Card>;
  if (data.items.length === 0) return null;
  return (
    <Card className="p-4" data-testid="cs-ws-invoices">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-base font-semibold text-slate-800">Factures</h2>
        <Freshness at={at} error={error} onRetry={reload} />
      </div>
      <ul className="space-y-3 sm:hidden">{data.items.map((i) => (
        <li key={i.id} id={`facture-m-${i.id}`} className={`rounded-lg border border-slate-200 p-3 ${highlight === i.id ? "bg-emerald-50/60" : ""}`} data-testid={`cs-inv-m-${i.id}`}>
          <p className="font-semibold text-slate-800">Facture {i.number}</p><p className="text-xs text-slate-500">{i.title} · {i.kind_label}</p>
          <div className="mt-2 grid grid-cols-2 gap-1 text-sm">
            <span className="text-slate-500">Montant</span><span className="text-right">{eurFr(i.total_ttc)} TTC{i.show_net ? ` · net ${eurFr(i.net_to_pay)}` : ""}</span>
            <span className="text-slate-500">Échéance</span><span className="text-right">{i.due_date ? dLong(i.due_date) : "Non renseignée"}</span>
            <span className="text-slate-500">Réglé</span><span className="text-right">{eurFr(i.paid)}</span>
            <span className="text-slate-500">Solde</span><span className="text-right font-semibold">{eurFr(i.due)}</span>
            <span className="text-slate-500">Statut</span><span className="flex flex-col items-end"><InvBadges i={i} /></span>
          </div>
          <div className="mt-2 border-t border-slate-100 pt-2"><InvActions i={i} token={token} onAsk={onAsk} reload={reload} /></div>
        </li>))}</ul>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[640px] text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2 pr-2">Document</th><th className="pr-2">Montant</th><th className="pr-2">Échéance</th><th className="pr-2">Réglé</th><th className="pr-2">Solde</th><th className="pr-2">Statut</th><th className="text-right">Actions</th></tr></thead>
          <tbody>{data.items.map((i) => (
            <tr key={i.id} id={`facture-${i.id}`} className={`border-b border-slate-100 align-top ${highlight === i.id ? "bg-emerald-50/60" : ""}`} data-testid={`cs-inv-${i.id}`}>
              <td className="py-2.5 pr-2"><p className="font-semibold text-slate-800">Facture {i.number}</p><p className="text-xs text-slate-500">{i.title} · {i.kind_label}</p></td>
              <td className="whitespace-nowrap pr-2"><p>{eurFr(i.total_ttc)} TTC</p>{i.show_net && <p className="text-xs text-slate-500">Net à payer {eurFr(i.net_to_pay)}</p>}</td>
              <td className="pr-2">{i.due_date ? dLong(i.due_date) : <span className="text-xs text-slate-400">Échéance non renseignée</span>}</td>
              <td className="whitespace-nowrap pr-2">{eurFr(i.paid)}</td>
              <td className="whitespace-nowrap pr-2 font-semibold" data-testid={`cs-inv-due-${i.id}`}>{eurFr(i.due)}</td>
              <td className="pr-2"><InvBadges i={i} /></td>
              <td className="text-right"><InvActions i={i} token={token} onAsk={onAsk} reload={reload} /></td>
            </tr>))}</tbody>
        </table>
      </div>
      <p className="mt-2 text-right text-sm text-slate-600">Reste à régler sur ce chantier : <strong data-testid="cs-ws-due">{eurFr(data.due)}</strong></p>
    </Card>
  );
}

export function ResteARegler({ token, onOpenInvoice }) {
  const { data, error, at, reload } = usePoll(() => axios.get(`${API}/clientspace/invoices/summary`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data), [token]);
  const [open, setOpen] = useState(false);
  if (!data) return (
    <Card className="p-5" data-testid="cs-reste">
      <h2 className="font-display text-xl font-bold text-[#0D2E24]">Reste à régler</h2>
      {error ? <p className="mt-2 text-sm text-rose-600" data-testid="cs-reste-error">Impossible de récupérer vos règlements. <button onClick={reload} className="font-medium underline">Réessayer</button></p>
        : <div className="mt-3 h-12 animate-pulse rounded bg-slate-100" />}
    </Card>);
  const next = data.items.find((x) => x.due_date === data.next_due_date);
  const notice = data.notices[0];
  return (
    <Card className="p-5 sm:p-6" data-testid="cs-reste">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 className="font-display text-xl font-bold text-[#0D2E24] sm:text-2xl">Reste à régler</h2>
        <Freshness at={at} error={error} onRetry={reload} />
      </div>
      {data.count === 0 ? <p className="mt-3 flex items-center gap-2 text-emerald-700" data-testid="cs-reste-ok"><Icons.CircleCheck className="h-5 w-5" />Vous êtes à jour de vos règlements</p> : (<>
        <div className="mt-3 grid gap-5 md:grid-cols-[1fr_auto_1fr]">
          <div>
            <p className="font-display text-4xl font-bold text-[#0D2E24] sm:text-5xl" data-testid="cs-reste-total">{eurFr(data.total_due)}</p>
            <p className="text-slate-500">{data.count} facture{data.count > 1 ? "s" : ""} à régler</p>
            <div className="mt-3 flex flex-wrap gap-6">
              {data.overdue > 0 && <div><p className="text-2xl font-bold text-orange-600" data-testid="cs-reste-overdue">{eurFr(data.overdue)}</p><p className="text-sm text-slate-500">Échéance dépassée</p></div>}
              {data.due_today > 0 && <div><p className="text-2xl font-bold text-amber-600">{eurFr(data.due_today)}</p><p className="text-sm text-slate-500">Dû aujourd'hui</p></div>}
              {data.future > 0 && <div><p className="text-2xl font-bold text-[#0D2E24]" data-testid="cs-reste-future">{eurFr(data.future)}</p><p className="text-sm text-slate-500">À venir</p></div>}
              {data.no_due_date > 0 && <div><p className="text-2xl font-bold text-slate-600">{eurFr(data.no_due_date)}</p><p className="text-sm text-slate-500">Échéance non renseignée</p></div>}
            </div>
          </div>
          <div className="hidden w-px bg-slate-200 md:block" />
          <div>
            {next ? <><p className="text-sm font-medium text-slate-500">Prochaine échéance</p><p className="font-display text-2xl font-bold text-[#0D2E24] sm:text-3xl">{dLong(next.due_date)}</p>
              <p className="text-sm text-slate-500">Facture {next.number} · {eurFr(next.due)}</p></> : <p className="text-sm text-slate-500">Aucune échéance à venir connue</p>}
            <Button variant="outline" onClick={() => setOpen(true)} className="mt-3 border-2 border-[#0D2E24] font-semibold text-[#0D2E24]" data-testid="cs-reste-open">Consulter les factures<Icons.ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
        </div>
        {notice && <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="cs-reste-notice">
          <span className="flex items-center gap-2"><Icons.Bell className="h-4 w-4" /><strong>Rappel :</strong> {notice.subject}</span>
          <button onClick={() => onOpenInvoice(notice.invoice_id)} className="font-medium underline">Voir la facture →</button></div>}
      </>)}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="cs-reste-panel">
          <DialogHeader><DialogTitle>Factures à régler</DialogTitle><DialogDescription>Selon les règlements enregistrés par notre équipe.</DialogDescription></DialogHeader>
          <ul className="divide-y divide-slate-100">{data.items.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-3" data-testid={`cs-reste-item-${i.id}`}>
              <div><p className="font-semibold text-slate-800">Facture {i.number}</p><p className="text-xs text-slate-500">{i.site_name} · {i.worksite_title}</p>
                <p className="text-xs text-slate-500">{i.due_date ? `Échéance ${dLong(i.due_date)}` : "Échéance non renseignée"}{i.overdue ? " · dépassée" : ""}</p></div>
              <div className="flex items-center gap-3"><span className="font-semibold">{eurFr(i.due)}</span>
                <Button size="sm" variant="outline" onClick={() => { setOpen(false); onOpenInvoice(i.id); }} data-testid={`cs-reste-go-${i.id}`}>Voir</Button></div>
            </li>))}</ul>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
